import { getFirebaseSingleton } from "@lib/firebase";
import { ActionType, onLoggedOut } from "@src/app/store/session";
import { exchangeCognitoTokenForFirebase } from "@src/appV2/Auth/cognito/tokenExchange";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

export function useIsAuthenticated(pathname: string, email: string): boolean {
  const dispatch = useDispatch();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const queryClient = useQueryClient();

  const cognitoAuth = getCognitoSingleton();
  const firebaseAuth = getFirebaseSingleton();

  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const isCognitoOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING, {
    defaultValue: false,
  });

  const isCognitoApiCallsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: false,
  });

  // When the user is authenticated with Cognito, we need to exchange the
  // Cognito token for a Firebase custom token to keep both tokens valid.
  useEffect(() => {
    const checkCognitoOnboardingSession = async () => {
      try {
        const cognitoUser = await cognitoAuth.getCurrentUserInfo();

        // No Cognito user authenticated, there is no need to exchange tokens
        if (!cognitoUser) {
          return;
        }

        // This means the logged in user has completed onboarding
        if (cognitoUser.cbhUserId) {
          logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORED, {
            uid: cognitoUser.uid,
            cbhUserId: cognitoUser.cbhUserId,
          });

          if (!firebaseAuth.currentUser) {
            // Exchange Cognito token for Firebase and establish session
            const firebaseCustomToken = await exchangeCognitoTokenForFirebase();
            await firebaseAuth.signInWithCustomToken(firebaseCustomToken);
          }

          setAuthenticated(true);

          return;
        }

        logEvent(APP_V2_APP_EVENTS.COGNITO_ONBOARDING_SESSION_RESTORED, {
          uid: cognitoUser.uid,
          phoneNumber: cognitoUser.phoneNumber,
        });

        const firebaseCustomToken = await exchangeCognitoTokenForFirebase();
        await firebaseAuth.signInWithCustomToken(firebaseCustomToken);

        dispatch({
          type: ActionType.SET_IS_SIGNUP,
          data: {
            isSignUp: true,
          },
        });

        dispatch({
          type: ActionType.LOGGED_IN,
          data: {
            profile: {
              phone: cognitoUser.phoneNumber,
            },
          },
        });

        setAuthenticated(true);
      } catch {
        // Silently fail - Firebase listener will handle auth state
      }
    };

    checkCognitoOnboardingSession();
  }, [dispatch, isCognitoOnboardingEnabled, isCognitoApiCallsEnabled, cognitoAuth, firebaseAuth]);

  useEffect(() => {
    const handleIdTokenChanged = async (user: unknown) => {
      try {
        logEvent(APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED, {
          isUserAlreadyAuthenticated: isAuthenticated,
          firebaseEmail: (user as { email?: string })?.email,
          email,
          userLastSignInTime: (user as { metadata?: { lastSignInTime?: string } })?.metadata
            ?.lastSignInTime,
        });

        const isInitAutoLogin = localStorage.getItem("isInitAutoLogin");
        localStorage.removeItem("isInitAutoLogin");

        if (
          isInitAutoLogin === "true" &&
          user &&
          pathname === "/welcome/login/emailVerify" &&
          !email
        ) {
          // this will return in signin page
          await firebaseAuth.signOut();
          indexedDB.deleteDatabase("firebaseLocalStorage");
          return;
        }

        if (user) {
          setAuthenticated(true);
          logEvent(APP_V2_APP_EVENTS.SIGNED_IN_TO_FIREBASE);

          await invalidateGetWorkerQuery(queryClient);
        } else {
          // Check if there's an active Cognito onboarding session before logging out
          // Cognito onboarding users don't have Firebase session yet
          // Note: We check directly without relying on feature flag because this callback
          // has a stale closure (empty dependency array in useEffect)
          try {
            const cognitoUser = await cognitoAuth.getCurrentUserInfo();

            // If user has Cognito session without cbhUserId, they're in onboarding
            // Don't trigger logout - let them continue onboarding
            if (cognitoUser && !cognitoUser.cbhUserId) {
              logEvent(APP_V2_APP_EVENTS.FIREBASE_LOGOUT_SKIPPED_FOR_COGNITO_ONBOARDING);
              return;
            }
          } catch {
            // If Cognito check fails, proceed with normal logout flow
          }

          setTimeout(async () => {
            logEvent(APP_V2_APP_EVENTS.SIGNED_OUT_FROM_FIREBASE);
            setAuthenticated(false);

            // clear worker stored in api hook
            await queryClient.cancelQueries([GET_WORKER_QUERY_KEY]);
            queryClient.removeQueries([GET_WORKER_QUERY_KEY]);
            onLoggedOut(dispatch);
          }, 1000);
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_ON_HANDLE_TOKEN_CHANGE_ERROR, {
          error,
          metadata: {
            email,
          },
        });

        // If we throw at any point after the firebase token
        // has been changed log the user out
        if (isDebuggingEnabled) {
          onLoggedOut(dispatch);
        }
      }
    };

    if (isAuthenticated) {
      logEvent(APP_V2_APP_EVENTS.FIREBASE_TOKEN_LISTENER_ATTACHED_AFTER_USER_WAS_AUTHENTICATED, {
        email: firebaseAuth.currentUser?.email,
      });
    }

    if (pathname === "/welcome/login/emailVerify" && !email) {
      localStorage.setItem("isInitAutoLogin", "true");
    }

    try {
      firebaseAuth.onIdTokenChanged(handleIdTokenChanged, (error) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_ERROR, {
          error,
        });
      });
      logEvent(APP_V2_APP_EVENTS.FIREBASE_ON_ID_TOKEN_CHANGED_LISTENER_ADDED);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.FIREBASE_LISTENER_HANDLER_ATTACHMENT_ERROR, {
        error,
        metadata: {
          email,
        },
      });
    }

    /**
     * This function should run once, but it depends on pathname and an email in the queryString.
     * We don't want to execute it for every path change, that's why ignoring rule-of-hooks here.
     * I believe we should refactor to not use pathname, but rather `window.location`
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isAuthenticated;
}
